import React, { useEffect, useState, useCallback } from 'react';
import { Card, Button, message, Steps, Tag, Space } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FileTextOutlined,
  DatabaseOutlined,
  VideoCameraOutlined,
  PlayCircleOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';
import { projectApi } from '../services/api';

const { Step } = Steps;

// 流程阶段配置
const STAGE_CONFIG = {
  script: {
    key: 'script',
    title: '剧本创作',
    icon: <FileTextOutlined />,
    description: '编写和上传剧本内容',
    color: '#1890ff',
    path: 'script'
  },
  assets: {
    key: 'assets',
    title: '资产管理',
    icon: <DatabaseOutlined />,
    description: '设计角色、道具、场景等资产',
    color: '#52c41a',
    path: 'assets'
  },
  storyboard: {
    key: 'storyboard',
    title: '分镜生成',
    icon: <VideoCameraOutlined />,
    description: '创建分镜和镜头规划',
    color: '#722ed1',
    path: 'storyboard'
  },
  production: {
    key: 'production',
    title: '视频制作',
    icon: <PlayCircleOutlined />,
    description: '生成视频和后期制作',
    color: '#fa8c16',
    path: 'production'
  },
  final: {
    key: 'final',
    title: '成片审核',
    icon: <CheckCircleOutlined />,
    description: '审核最终成片并提供反馈',
    color: '#13c2c2',
    path: 'final-review'
  }
};

// 阶段顺序
const STAGE_ORDER = ['script', 'assets', 'storyboard', 'production', 'final'];

const ProjectManagementPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(false);

  // 加载项目数据
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const projectRes = await projectApi.getById(id);
      setProject(projectRes.data);
    } catch (error) {
      message.error('加载项目数据失败');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id, loadData]);

  // 处理进入阶段
  const handleEnterStage = (stageKey) => {
    navigate(`/project/${id}/${STAGE_CONFIG[stageKey].path}`);
  };

  // 渲染阶段卡片
  const renderStageCard = (stageKey) => {
    const config = STAGE_CONFIG[stageKey];

    return (
      <Card
        key={stageKey}
        style={{
          marginBottom: 16,
          background: '#1a1a24',
          border: '1px solid #2a2a3a',
          borderLeft: `4px solid ${config.color}`,
          borderRadius: 8
        }}
        title={
          <Space>
            <span style={{ fontSize: 20, color: config.color }}>{config.icon}</span>
            <span style={{ fontWeight: 'bold', fontSize: 16, color: '#e8e8ec' }}>{config.title}</span>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<ArrowRightOutlined />}
            onClick={() => handleEnterStage(stageKey)}
          >
            进入
          </Button>
        }
      >
        <div style={{ color: '#a0a0b0' }}>{config.description}</div>
      </Card>
    );
  };

  if (!project) {
    return <div style={{ padding: 24, background: '#0d0d12', color: '#e8e8ec' }}>加载中...</div>;
  }

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto', background: '#0d0d12', minHeight: '100%' }}>
      {/* 项目标题 */}
      <Card style={{
        marginBottom: 24,
        background: '#1a1a24',
        border: '1px solid #2a2a3a',
        borderRadius: 12
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ margin: 0, marginBottom: 8, color: '#e8e8ec' }}>{project.name}</h1>
            <Space>
              <Tag color="#252532" style={{ color: '#e8e8ec', border: '1px solid #2a2a3a' }}>{project.style}</Tag>
              <Tag color="#252532" style={{ color: '#e8e8ec', border: '1px solid #2a2a3a' }}>{project.video_ratio}</Tag>
              <Tag color="#252532" style={{ color: '#e8e8ec', border: '1px solid #2a2a3a' }}>{project.video_resolution || '720P'}</Tag>
            </Space>
          </div>
        </div>
        {project.description && (
          <div style={{ marginTop: 12, color: '#a0a0b0' }}>{project.description}</div>
        )}
      </Card>

      {/* 流程步骤条 */}
      <Card style={{
        marginBottom: 24,
        background: '#1a1a24',
        border: '1px solid #2a2a3a',
        borderRadius: 12
      }}>
        <Steps current={0}>
          <Step title="剧本创作" icon={<FileTextOutlined />} />
          <Step title="资产管理" icon={<DatabaseOutlined />} />
          <Step title="分镜生成" icon={<VideoCameraOutlined />} />
          <Step title="视频制作" icon={<PlayCircleOutlined />} />
          <Step title="成片审核" icon={<CheckCircleOutlined />} />
        </Steps>
      </Card>

      {/* 工作流阶段卡片 */}
      <Card
        title={<span style={{ color: '#e8e8ec' }}>制作流程</span>}
        loading={loading}
        style={{
          background: '#1a1a24',
          border: '1px solid #2a2a3a',
          borderRadius: 12,
          marginBottom: 24
        }}
        headStyle={{ borderBottom: '1px solid #2a2a3a' }}
      >
        {STAGE_ORDER.map(stageKey => renderStageCard(stageKey))}
      </Card>

      {/* 项目设置入口 */}
      <Card
        hoverable
        onClick={() => navigate(`/project/${id}/settings`)}
        style={{
          textAlign: 'center',
          background: '#1a1a24',
          border: '1px solid #2a2a3a',
          borderRadius: 12
        }}
      >
        <SettingOutlined style={{ fontSize: 48, color: '#00d4aa', marginBottom: 16 }} />
        <div style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 8, color: '#e8e8ec' }}>项目设置</div>
        <div style={{ color: '#a0a0b0' }}>管理项目基本信息和配置</div>
      </Card>
    </div>
  );
};

export default ProjectManagementPage;
